import React, { useCallback, useEffect, useRef, useState } from "react";
import Hero from "../Assets/Hero.png";
import AboutUsImage from "../Assets/AboutUs.png";
import Whychoose1 from "../Assets/Whychoose1.png";
import Whychoose2 from "../Assets/Whychoose2.png";
import Whychoose3 from "../Assets/Whychoose3.png";
import OurServicesBG from "../Assets/OurServicesBG.png";
import Services1 from "../Assets/Services1.png";
import Services2 from "../Assets/Services2.png";
import Services3 from "../Assets/Services3.png";
import ProfileImage from "../Assets/ProfileDesk.png";
import ProfileMob from "../Assets/ProfileMob.png";

const SECTION_HEADING_CLASS = "text-[26px] font-bold leading-[1.15] tracking-[-0.02em] sm:text-[30px] md:text-[44px]";
const BODY_TEXT_CLASS = "text-[15px] leading-[1.6] sm:text-[16px] md:text-[20px]";
const CARD_HEADING_CLASS = "text-[22px] font-semibold leading-tight sm:text-[24px] md:text-[34px]";
const CARD_BODY_CLASS = "text-[15px] leading-[1.55] sm:text-[16px] md:text-[20px]";
const FORM_ANIMATION_MS = 300;

function Home() {
    const [showContactForm, setShowContactForm] = useState(false);
    const [contactFormMotion, setContactFormMotion] = useState("idle");
    const [formData, setFormData] = useState({ name: "", email: "", address: "" });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState("");
    const desktopFormRef = useRef(null);
    const mobileFormRef = useRef(null);
    const closeTimeoutRef = useRef(null);

    const openContactForm = useCallback(() => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setShowContactForm(true);
        setContactFormMotion("enter");
    }, []);

    const closeContactForm = useCallback(() => {
        if (!showContactForm || closeTimeoutRef.current) return;
        setContactFormMotion("exit");
        closeTimeoutRef.current = setTimeout(() => {
            setShowContactForm(false);
            setContactFormMotion("idle");
            closeTimeoutRef.current = null;
        }, FORM_ANIMATION_MS);
    }, [showContactForm]);

    const WEBHOOK_URL =
        "https://script.google.com/macros/s/AKfycbxfInJ8l69lgea_-SxpuQzYn31iXQF_JXju0IkNxMr3rufVE4qED5SzdcMoigsG6m9E/exec";

    const handleChange = (field) => (event) => {
        setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        setSubmitMessage("");

        try {
            const body = new URLSearchParams({
                name: formData.name,
                email: formData.email,
                address: formData.address,
            });

            const response = await fetch(WEBHOOK_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
                },
                body,
            });

            const text = await response.text();

            if (!response.ok) {
                throw new Error(text || "Request failed");
            }

            setSubmitMessage("Thanks for reaching out!\nWe’ll get back to you within 24 hours.");
            setFormData({ name: "", email: "", address: "" });
        } catch (error) {
            setSubmitMessage(
                error?.message || "Sorry, we couldn't submit your details. Please try again."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const contactForm = (
        <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-3">
                <input
                    type="text"
                    placeholder="Enter your name"
                    className="w-full rounded-lg border border-white/50 bg-white px-4 py-2.5 text-[15px] text-[#111111] outline-none placeholder:text-[#717171] focus:border-[#8DBDFF] sm:px-5 sm:text-[17px]"
                    value={formData.name}
                    onChange={handleChange("name")}
                    required
                />
                <input
                    type="email"
                    placeholder="Enter your email address"
                    className="w-full rounded-lg border border-white/50 bg-white px-4 py-2.5 text-[15px] text-[#111111] outline-none placeholder:text-[#717171] focus:border-[#8DBDFF] sm:px-5 sm:text-[17px]"
                    value={formData.email}
                    onChange={handleChange("email")}
                    required
                />
                <textarea
                    rows={3}
                    placeholder="Enter your message"
                    className="w-full resize-none rounded-lg border border-white/50 bg-white px-4 py-2.5 text-[15px] text-[#111111] outline-none placeholder:text-[#717171] focus:border-[#8DBDFF] sm:px-5 sm:text-[17px]"
                    value={formData.address}
                    onChange={handleChange("address")}
                    required
                />
            </div>

            <button
                type="submit"
                className="w-full rounded-lg bg-[#002B69] px-5 py-2.5 text-[16px] font-medium text-white transition hover:bg-[#003985] md:text-[18px] disabled:cursor-not-allowed disabled:opacity-70"
                disabled={isSubmitting}
            >
                {isSubmitting ? "SENDING..." : "SEND MESSAGE"}
            </button>

            <p className="whitespace-pre-line text-center text-[15px] text-white/95">
                {submitMessage
                    ? submitMessage
                    : "Get in touch with us. Our team is here to assist you."}
            </p>
        </form>
    );

    useEffect(() => {
        window.addEventListener("openContactForm", openContactForm);
        return () => window.removeEventListener("openContactForm", openContactForm);
    }, [openContactForm]);

    useEffect(() => {
        if (!showContactForm || contactFormMotion !== "enter") return undefined;
        const rafId = requestAnimationFrame(() => setContactFormMotion("open"));
        return () => cancelAnimationFrame(rafId);
    }, [showContactForm, contactFormMotion]);

    useEffect(() => {
        if (!showContactForm) return undefined;
        const handleClickOutside = (event) => {
            const insideDesktop = desktopFormRef.current?.contains(event.target);
            const insideMobile = mobileFormRef.current?.contains(event.target);
            if (!insideDesktop && !insideMobile) closeContactForm();
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showContactForm, closeContactForm]);

    return (
        <>
            <div
                id="home"
                className="relative w-full overflow-hidden"
            >
                <div className="relative min-h-[520px] w-full overflow-hidden sm:min-h-0 sm:aspect-[8000/2944]">
                    <img
                        src={Hero}
                        alt="Global talent hero"
                        className="absolute inset-0 h-full w-full object-cover object-[88%_top] sm:object-contain sm:object-top"
                    />
                    <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(7,19,38,0.74)_0%,rgba(7,19,38,0.5)_34%,rgba(7,19,38,0.16)_58%,rgba(7,19,38,0)_100%)] sm:hidden" />
                </div>
                <div className="absolute inset-0 z-20 px-4 sm:px-6 lg:px-[4%] xl:px-[6%]">
                    <div className="grid h-full grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-6">
                        <div className="max-w-[320px] space-y-4 pt-8 sm:max-w-2xl sm:space-y-5 sm:pt-0 md:space-y-7">
                            <h1 className="text-[34px] font-medium leading-[0.98] tracking-tight text-white sm:text-[48px] md:text-[72px] xl:text-[84px]">
                                Global Talent,
                                <br />
                                Simplified.
                            </h1>

                            <p className="max-w-[300px] text-[15px] font-normal leading-[1.45] text-white sm:max-w-[760px] sm:text-[16px] md:text-[20px]">
                                We connect ambitious businesses with top-tier Sri Lankan professionals.
                                Build your dream team remotely while we handle the rest.
                            </p>

                            <button
                                className="inline-flex w-full max-w-[272px] items-center justify-center whitespace-nowrap rounded-2xl px-5 py-3 text-center text-[14px] font-medium tracking-wide sm:w-auto sm:min-w-[280px] sm:max-w-[320px] sm:px-8 sm:py-4 sm:text-[16px] md:px-10 md:py-5 md:text-[18px]"
                                style={{
                                    backgroundColor: "#F4971A",
                                    backgroundImage: "linear-gradient(180deg, #F4971A 0%, #FFBA3B 100%)",
                                    color: "#003985",
                                }}
                                type="button"
                                onClick={openContactForm}
                            >
                                START YOUR JOURNEY WITH US
                            </button>
                        </div>

                        {showContactForm && (
                            <div className="hidden w-full max-w-[480px] justify-self-end lg:block">
                                <div
                                    ref={desktopFormRef}
                                    className={`rounded-[18px] border border-white/35 bg-white/12 p-6 shadow-[0_20px_50px_rgba(0,0,0,0.45)] backdrop-blur-md transition-[transform,opacity] duration-300 ease-out ${contactFormMotion === "open"
                                        ? "translate-x-0 opacity-100"
                                        : "translate-x-10 opacity-0"
                                        }`}
                                >
                                    <div className="relative mb-4 flex items-center justify-center">
                                        <h3 className="text-center text-[30px] font-medium tracking-normal text-white md:text-[32px]">
                                            LET&apos;S CONNECT
                                        </h3>
                                        <button
                                            type="button"
                                            aria-label="Close form"
                                            onClick={closeContactForm}
                                            className="absolute right-0 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/50 text-white/90 transition hover:bg-white/15"
                                        >
                                            ×
                                        </button>
                                    </div>
                                    {contactForm}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showContactForm && (
                <div
                    className={`fixed inset-0 z-50 bg-black/60 px-4 py-8 transition-opacity duration-300 ease-out lg:hidden ${contactFormMotion === "open" ? "opacity-100" : "opacity-0"
                        }`}
                    onClick={closeContactForm}
                >
                    <div className="mx-auto flex h-full w-full max-w-[480px] items-center">
                        <div
                            ref={mobileFormRef}
                            className={`max-h-full w-full overflow-y-auto rounded-[18px] border border-white/35 bg-white/12 p-3 shadow-[0_20px_50px_rgba(0,0,0,0.55)] backdrop-blur-md transition-[transform,opacity] duration-300 ease-out sm:p-6 ${contactFormMotion === "open"
                                ? "translate-x-0 opacity-100"
                                : "translate-x-10 opacity-0"
                                }`}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="relative mb-3 flex items-center justify-center">
                                <h3 className="text-center text-[24px] font-medium tracking-normal text-white sm:text-[30px] md:text-[32px]">
                                    LET&apos;S CONNECT
                                </h3>
                                <button
                                    type="button"
                                    aria-label="Close form"
                                    onClick={closeContactForm}
                                    className="absolute right-0 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/50 text-white/90 transition hover:bg-white/15"
                                >
                                    ×
                                </button>
                            </div>
                            {contactForm}
                        </div>
                    </div>
                </div>
            )}

            <section id="about" className="w-full border-y border-[#D0D0D0] bg-[#ECECEC]">
                <div className="mx-auto grid w-full max-w-[1860px] grid-cols-1 items-center gap-9 px-5 py-10 sm:px-6 md:px-10 md:py-14 lg:grid-cols-2 lg:gap-14 lg:px-16 xl:px-20">
                    <div className="space-y-6 md:space-y-8 lg:space-y-10">
                        <div className="flex flex-wrap items-center gap-4 sm:gap-5">
                            <span className="h-[2px] w-16 bg-[#454545] sm:w-24" />
                            <span className="rounded-full bg-[#D8D8D8] px-6 py-2 text-[16px] leading-none font-medium text-[#2F2F2F] md:text-[18px]">
                                About Us
                            </span>
                        </div>

                        <h2 className={`${SECTION_HEADING_CLASS} bg-gradient-to-r from-[#123C70] to-[#2273D6] bg-clip-text text-transparent`}>
                            Bridging Global Demand
                            <br />
                            With Sri Lankan Excellence
                        </h2>

                        <p className={`${BODY_TEXT_CLASS} max-w-[920px] text-[#171717]`}>
                            SimpliConnect was founded to bridge the gap between global demand and Sri Lankan excellence.
                            We don't just provide resources; we build the infrastructure handling recruitment, legal
                            compliance, and payroll so you can focus on growing your business.
                        </p>
                    </div>

                    <div className="w-full lg:justify-self-end">
                        <img
                            src={AboutUsImage}
                            alt="About SimpliConnect"
                            className="w-full max-w-[820px] object-cover"
                        />
                    </div>
                </div>
            </section>

            <section id="why-choose-us" className="w-full bg-[#ECECEC] py-14 md:py-20">
                <div className="mx-auto w-full max-w-[1860px] px-6 md:px-10 lg:px-16 xl:px-20">
                    <h2 className={`${SECTION_HEADING_CLASS} text-center bg-gradient-to-r from-[#123C70] to-[#2273D6] bg-clip-text text-transparent`}>
                        Why Choose Us?
                    </h2>

                    <div className="mt-8 grid grid-cols-1 gap-6 sm:mt-10 sm:gap-8 lg:grid-cols-3">
                        <article className="flex min-h-0 flex-col items-center justify-start bg-[#EFEFEF] px-6 py-9 text-center shadow-[0_4px_14px_rgba(0,0,0,0.18)] sm:px-8 sm:py-10 md:py-12">
                            <img src={Whychoose1} alt="Top 1% Talent" className="h-16 w-16 object-contain sm:h-20 sm:w-20 md:h-[88px] md:w-[88px]" />
                            <h3 className={`${CARD_HEADING_CLASS} mt-6 sm:mt-8 text-[#173D77]`}>
                                Top 1% Talent
                            </h3>
                            <p className={`${CARD_BODY_CLASS} mt-3 sm:mt-5 max-w-[430px] text-[#2B2B2B]`}>
                                Access rigorously vetted Sri Lankan professionals tailored to your specific technical
                                needs.
                            </p>
                        </article>

                        <article className="flex min-h-0 flex-col items-center justify-start bg-[#EFEFEF] px-6 py-9 text-center shadow-[0_4px_14px_rgba(0,0,0,0.18)] sm:px-8 sm:py-10 md:py-12">
                            <img src={Whychoose2} alt="Legal and Payroll Ease" className="h-16 w-16 object-contain sm:h-20 sm:w-20 md:h-[88px] md:w-[88px]" />
                            <h3 className={`${CARD_HEADING_CLASS} mt-6 sm:mt-8 text-[#173D77]`}>
                                Legal & Payroll Ease
                            </h3>
                            <p className={`${CARD_BODY_CLASS} mt-3 sm:mt-5 max-w-[460px] text-[#2B2B2B]`}>
                                We handle all local compliance, taxes, and international payroll so you don't have to.
                            </p>
                        </article>

                        <article className="flex min-h-0 flex-col items-center justify-start bg-[#EFEFEF] px-6 py-9 text-center shadow-[0_4px_14px_rgba(0,0,0,0.18)] sm:px-8 sm:py-10 md:py-12">
                            <img src={Whychoose3} alt="Cost-Effective Scaling" className="h-16 w-16 object-contain sm:h-20 sm:w-20 md:h-[88px] md:w-[88px]" />
                            <h3 className={`${CARD_HEADING_CLASS} mt-6 sm:mt-8 text-[#173D77]`}>
                                Cost-Effective Scaling
                            </h3>
                            <p className={`${CARD_BODY_CLASS} mt-3 sm:mt-5 max-w-[470px] text-[#2B2B2B]`}>
                                Reduce your operational overhead by up to 60% without sacrificing work quality.
                            </p>
                        </article>
                    </div>
                </div>
            </section>

            <section id="services" className="relative w-full overflow-hidden">
                <img
                    src={OurServicesBG}
                    alt="Our Services background"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,57,115,0.62),rgba(16,57,115,0.26),rgba(16,57,115,0.38))]" />

                <div className="relative z-10 mx-auto w-full max-w-[1860px] px-5 py-10 sm:px-6 sm:py-12 md:px-10 md:py-20 lg:px-16 xl:px-20">
                    <h2 className={`${SECTION_HEADING_CLASS} text-white`}>
                        Our Services
                    </h2>
                    <p className={`${BODY_TEXT_CLASS} mt-3 sm:mt-4 text-white/95`}>
                        How It Works? Simple Steps To Building Your Dream Team
                    </p>

                    <div className="mt-8 grid grid-cols-1 gap-10 sm:mt-10 sm:gap-12 lg:grid-cols-3 lg:gap-8 xl:gap-10">
                        <article className="relative bg-white/80 px-3 pb-4 pt-10 shadow-[0_8px_20px_rgba(0,0,0,0.2)] backdrop-blur-[1px] sm:px-4 sm:pb-5 sm:pt-11 md:px-5 md:pb-6 md:pt-12">
                            <div className="absolute -top-5 left-3 h-10 w-10 sm:-top-6 sm:left-4 sm:h-12 sm:w-12 md:-top-7 md:left-5 md:h-16 md:w-16">
                                <img src={Services1} alt="Source and Screen" className="h-full w-full object-contain" />
                            </div>
                            <h3 className={`${CARD_HEADING_CLASS} mt-1 text-[#133E78] text-[16px] sm:text-[18px] md:text-[22px]`}>
                                Source & Screen
                            </h3>
                            <p className={`${CARD_BODY_CLASS} mt-2 sm:mt-3 max-w-[540px] text-[#133E78] text-[13px] sm:text-[14px] md:text-[16px]`}>
                                We headhunt and pre-filter the best candidates for you to interview and select.
                            </p>
                        </article>

                        <article className="relative bg-white/80 px-3 pb-4 pt-10 shadow-[0_8px_20px_rgba(0,0,0,0.2)] backdrop-blur-[1px] sm:px-4 sm:pb-5 sm:pt-11 md:px-5 md:pb-6 md:pt-12">
                            <div className="absolute -top-5 left-3 h-10 w-10 sm:-top-6 sm:left-4 sm:h-12 sm:w-12 md:-top-7 md:left-5 md:h-16 md:w-16">
                                <img src={Services2} alt="Onboard" className="h-full w-full object-contain" />
                            </div>
                            <h3 className={`${CARD_HEADING_CLASS} mt-1 text-[#133E78] text-[16px] sm:text-[18px] md:text-[22px]`}>
                                Onboard
                            </h3>
                            <p className={`${CARD_BODY_CLASS} mt-2 sm:mt-3 max-w-[560px] text-[#133E78] text-[13px] sm:text-[14px] md:text-[16px]`}>
                                We manage all legal contracts and ensure the remote infrastructure is ready for a day
                                1 start.
                            </p>
                        </article>

                        <article className="relative bg-white/80 px-3 pb-4 pt-10 shadow-[0_8px_20px_rgba(0,0,0,0.2)] backdrop-blur-[1px] sm:px-4 sm:pb-5 sm:pt-11 md:px-5 md:pb-6 md:pt-12">
                            <div className="absolute -top-5 left-3 h-10 w-10 sm:-top-6 sm:left-4 sm:h-12 sm:w-12 md:-top-7 md:left-5 md:h-16 md:w-16">
                                <img src={Services3} alt="Manage and Support" className="h-full w-full object-contain" />
                            </div>
                            <h3 className={`${CARD_HEADING_CLASS} mt-1 text-[#133E78] text-[16px] sm:text-[18px] md:text-[22px]`}>
                                Manage & Support
                            </h3>
                            <p className={`${CARD_BODY_CLASS} mt-2 sm:mt-3 max-w-[560px] text-[#133E78] text-[13px] sm:text-[14px] md:text-[16px]`}>
                                We provide ongoing HR support and administrative care to ensure long-term team
                                success.
                            </p>
                        </article>
                    </div>
                </div>
            </section>

            <section id="profile-message" className="relative w-full overflow-hidden bg-[#EEF2F5]">
                <picture>
                    <source media="(max-width: 767px)" srcSet={ProfileMob} />
                    <img
                        src={ProfileImage}
                        alt="Director profile"
                        className="mx-auto block h-auto w-full max-w-[1920px]"
                    />
                </picture>
            </section>
        </>
    );
}

export default Home;
